import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, UsersThree, CalendarDots, Heart, ChartLineUp, Storefront } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, StatusBadge } from '../../components/admin/AdminComponents';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <span className="text-[11px] text-[#555] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
    </div>
  );
}

export default function AdminCommunity() {
  const [stats, setStats] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState('leagues');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, lRes, gRes] = await Promise.all([
          axios.get(`${API}/api/admin-console/community-stats`, { withCredentials: true }),
          axios.get(`${API}/api/admin-console/leagues?limit=50`, { withCredentials: true }),
          axios.get(`${API}/api/admin-console/recurring-groups?limit=50`, { withCredentials: true }),
        ]);
        setStats(sRes.data);
        setLeagues(lRes.data.leagues || []);
        setGroups(gRes.data.groups || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/api/admin-console/${type}/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${status}`);
      window.location.reload();
    } catch { toast.error('Failed'); }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="admin-community-loading">
      <div className="h-7 w-56 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-community-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Community</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Leagues, recurring groups, and local community oversight</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
          <StatCard icon={Trophy} label="Leagues" value={stats.total_leagues} accent="#F59E0B" />
          <StatCard icon={Trophy} label="Active Leagues" value={stats.active_leagues} accent="#10B981" />
          <StatCard icon={UsersThree} label="Groups" value={stats.total_groups} accent="#9D4CDD" />
          <StatCard icon={UsersThree} label="Active Groups" value={stats.active_groups} accent="#10B981" />
          <StatCard icon={CalendarDots} label="Seasons" value={stats.total_seasons} accent="#3B82F6" />
          <StatCard icon={CalendarDots} label="Active Seasons" value={stats.active_seasons} accent="#10B981" />
          <StatCard icon={Heart} label="Shop Favorites" value={stats.total_shop_favorites} accent="#EF4444" />
        </div>
      )}

      <div className="flex gap-1 mb-5 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[{ id: 'leagues', label: 'Leagues', icon: Trophy }, { id: 'groups', label: 'Groups', icon: UsersThree }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === t.id ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'text-[#555] hover:text-[#999]'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={13} weight={tab === t.id ? 'duotone' : 'regular'} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'leagues' && (
        <div className="space-y-2">
          {leagues.length === 0 ? (
            <p className="text-[#444] text-center py-8">No leagues yet</p>
          ) : leagues.map(lg => (
            <div key={lg.id} className="flex items-center gap-4 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-4 py-3"
              data-testid={`admin-league-${lg.id}`}>
              <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                <Trophy size={15} weight="duotone" className="text-[#F59E0B]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[13px] font-semibold text-white truncate">{lg.name}</h3>
                <p className="text-[11px] text-[#555]">{lg.game_id} | {lg.league_type} | {lg.member_count} members</p>
              </div>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                lg.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                lg.status === 'paused' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20' :
                'bg-white/5 text-[#555] border border-white/10'
              }`}>{lg.status}</span>
              <div className="flex gap-1">
                {lg.status === 'active' && (
                  <button onClick={() => updateStatus('leagues', lg.id, 'paused')}
                    className="px-2 py-1 text-[10px] rounded bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20 hover:bg-[#F59E0B]/20"
                    data-testid={`pause-league-${lg.id}`}>Pause</button>
                )}
                {lg.status !== 'active' && (
                  <button onClick={() => updateStatus('leagues', lg.id, 'active')}
                    className="px-2 py-1 text-[10px] rounded bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 hover:bg-[#10B981]/20"
                    data-testid={`activate-league-${lg.id}`}>Activate</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'groups' && (
        <div className="space-y-2">
          {groups.length === 0 ? (
            <p className="text-[#444] text-center py-8">No recurring groups yet</p>
          ) : groups.map(g => (
            <div key={g.id} className="flex items-center gap-4 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-4 py-3"
              data-testid={`admin-group-${g.id}`}>
              <div className="w-8 h-8 rounded-lg bg-[#9D4CDD]/10 flex items-center justify-center flex-shrink-0">
                <UsersThree size={15} weight="duotone" className="text-[#9D4CDD]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[13px] font-semibold text-white truncate">{g.name}</h3>
                <p className="text-[11px] text-[#555]">{g.city}{g.state ? `, ${g.state}` : ''} | {g.group_type} | {g.member_count} members</p>
              </div>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                g.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                g.status === 'paused' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20' :
                'bg-white/5 text-[#555] border border-white/10'
              }`}>{g.status}</span>
              <div className="flex gap-1">
                {g.status === 'active' && (
                  <button onClick={() => updateStatus('recurring-groups', g.id, 'paused')}
                    className="px-2 py-1 text-[10px] rounded bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20 hover:bg-[#F59E0B]/20"
                    data-testid={`pause-group-${g.id}`}>Pause</button>
                )}
                {g.status !== 'active' && (
                  <button onClick={() => updateStatus('recurring-groups', g.id, 'active')}
                    className="px-2 py-1 text-[10px] rounded bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 hover:bg-[#10B981]/20"
                    data-testid={`activate-group-${g.id}`}>Activate</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
